import { env } from './config/env'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { APIProvider } from '@vis.gl/react-google-maps'
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query'
import './lib/zod-locale'
import { reportQueryError } from './lib/sentry/report-query-error'
import { scrubEventBeforeSend } from './lib/sentry/scrub-event'
import {
  isSuppressablePreloadError,
  isUnhandledChunkImportRejection,
  shouldRetryQuery,
} from './shared/utils/network-error-utils'
import { SidebarProvider } from './components/ui/sidebar'
import { GuidedTourProvider } from './lib/guided-tour/tour-provider'
import { initAuthBridge } from './lib/auth-bridge'
import { AppToaster } from './components/shared/app-toaster'
import { toast } from './components/shared/toasts'
import './App.css' //need to remove afterwards?
import App from './App'

// Register the single canonical onAuthStateChange listener at client init
// (#1112). This is what actually drives supabase.realtime.setAuth + the Zustand
// auth store from GoTrue session state — without this call the bridge is dead
// code and the Realtime socket is never re-authed (stays anon -> RLS rejects
// all postgres_changes).
initAuthBridge()

// Stale-chunk recovery after a Cloudflare Pages rollover (#1379). A user on the
// old index.html requests a lazy-route chunk whose hash no longer exists -> Vite
// fires `vite:preloadError`. Offer a one-click reload to fetch the fresh
// index.html + current chunks. We do NOT auto-reload: a silent reload could
// interrupt a mid-form action in this admin app. Guarded so it shows once.
//
// 2026-07-10 outage hardening: a chunk import can also fail because the browser
// HTTP cache holds a POISONED entry — the SPA-fallback index.html stored under
// an /assets/*.js URL with `max-age=31536000, immutable` (Pages applies the
// path-scoped /assets/* rule from public/_headers even to the fallback it
// serves during a deploy-propagation window). `immutable` means a normal reload
// never revalidates it, so the reload prompt alone loops forever for those
// users. Before reloading, read back every /assets/ response this page has
// seen (`only-if-cached` never hits the network) and force-refetch the ones
// whose content-type doesn't match their extension, replacing the poisoned
// cache entries with fresh ones from the edge.
async function healPoisonedAssetCache(): Promise<number> {
  const assetUrls = [
    ...new Set(
      performance
        .getEntriesByType('resource')
        .map((entry) => entry.name)
        .filter((url) => url.startsWith(`${window.location.origin}/assets/`))
    ),
  ]
  const results = await Promise.allSettled(
    assetUrls.map(async (url) => {
      const cached = await fetch(url, {
        cache: 'only-if-cached',
        mode: 'same-origin',
      })
      const contentType = cached.headers.get('content-type') ?? ''
      const poisoned =
        (url.endsWith('.js') && !contentType.includes('javascript')) ||
        (url.endsWith('.css') && !contentType.includes('css'))
      if (!poisoned) return false
      const replacement = await fetch(url, {
        cache: 'reload',
        mode: 'same-origin',
      })
      const replacementType = replacement.headers.get('content-type') ?? ''
      // Only count the entry as healed when the replacement is actually the
      // right type — if the edge is still serving the poisoned body there is
      // nothing more we can do client-side, and claiming "healed" would skew
      // the Sentry signal this count exists to provide.
      return url.endsWith('.js')
        ? replacementType.includes('javascript')
        : replacementType.includes('css')
    })
  )
  return results.filter(
    (result) => result.status === 'fulfilled' && result.value
  ).length
}

let staleVersionToastShown = false
window.addEventListener('vite:preloadError', (event) => {
  // Suppress Vite's default rethrow (before the show-once guard) ONLY for the
  // dependency preloads, which Vite reports through the same event and after
  // which `baseModule()` still runs — we recover via the reload prompt below,
  // so letting those surface would only add uncaught-error noise to Sentry.
  // Never for the module fetch itself: Vite's helper runs
  // `baseModule().catch(handlePreloadError)`, so a prevented default makes the
  // dynamic import RESOLVE WITH `undefined` instead of rejecting. The route
  // tree then reads `.Route` off that undefined module and the router reads
  // `.component` off route options it never got — the "Cannot read properties
  // of undefined" TypeErrors in Sentry (HARMONY-WEB-2R, then HARMONY-WEB-2V),
  // on top of the error page the user already gets. Letting that one throw
  // hands the router the real rejection instead, and the reload prompt below
  // still fires.
  //
  // Suppressing by what CAN be suppressed, not by what cannot: naming the
  // browsers' module-fetch wordings (HARMONY-WEB-2R) left everything they do
  // not cover on the crashing side.
  const reason = (event as Event & { payload?: unknown }).payload
  if (isSuppressablePreloadError(reason)) event.preventDefault()
  if (staleVersionToastShown) return
  staleVersionToastShown = true
  const healing = healPoisonedAssetCache()
    .then((healedCount) => {
      if (healedCount > 0) {
        // Visibility into how many users still carry poisoned entries — drives
        // the decision to ship a chunk-renaming rescue deploy if it lingers.
        Sentry.captureMessage(
          `vite:preloadError — healed ${healedCount} poisoned asset cache entries`,
          'warning'
        )
      }
    })
    .catch(() => undefined) // best-effort: the reload prompt still applies
  toast.info('Une nouvelle version est disponible', undefined, {
    action: {
      label: 'Recharger',
      onClick: () => {
        // Bounded wait for the heal so the reload picks up the replaced
        // entries; reloading mid-heal would resurrect the poisoned ones.
        void Promise.race([
          healing,
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]).then(() => window.location.reload())
      },
    },
    duration: Infinity,
  })
})

// `getClient()` is undefined only on the first evaluation of this module. Vite
// re-imports the entry with a fresh `?t=` query when an HMR update bubbles up
// to a module with no accept boundary, so everything here runs again in the
// SAME page: the second `Sentry.init` builds a second Replay, `new Replay()`
// throws "Multiple Sentry Session Replay instances are not supported"
// (HARMONY-WEB-27 / HARMONY-WEB-4), and that throw aborts the rest of this
// module — the queryClient and the root render below never run on that pass.
// Same reason the render is guarded by `rootElement.innerHTML`: one per page.
if (env.VITE_SENTRY_DSN && !Sentry.getClient()) {
  const sentryEnv = env.VITE_SENTRY_ENVIRONMENT || env.VITE_APP_ENV
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: sentryEnv,
    // Same value the sentryVitePlugin uses for release.name (vite.config.ts) —
    // required for uploaded source maps to associate with these events.
    release: env.VITE_RELEASE || undefined,
    // Function form REQUIRED: a literal array here REPLACES the SDK's default
    // integrations (global error/unhandled-rejection handlers, etc). The
    // function form appends to defaults instead.
    integrations: (defaults) => [
      ...defaults,
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: sentryEnv === 'production' ? 0.1 : 1.0,
    // Propagate traces to our API
    tracePropagationTargets: ['localhost', env.VITE_API_URL],
    // Session Replay: 10% of sessions, 100% on error
    replaysSessionSampleRate: sentryEnv === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    // PII scrubbing (OBS-9): drop deny-listed keys from extra/tags/span
    // attributes/breadcrumb data on every event + transaction, on top of the
    // server-side scrubbers. See ./lib/sentry/scrub-event.ts for the policy.
    // A stale chunk that escaped the router unhandled is dropped first — but
    // only once the reload prompt above was actually shown, since that prompt
    // is what owns it (HARMONY-WEB-6). Vite dispatches `vite:preloadError`
    // before it rethrows, so the flag is set by the time the rejection lands.
    beforeSend: (event, hint) =>
      staleVersionToastShown && isUnhandledChunkImportRejection(event, hint)
        ? null
        : scrubEventBeforeSend(event),
    beforeSendTransaction: (event) => scrubEventBeforeSend(event),
  })
}

export const queryClient = new QueryClient({
  // Forward handled query/mutation failures to Sentry. React-query catches
  // these rejections, so without this bridge they show a toast but never reach
  // monitoring. Per-query/mutation onError handlers still run in addition.
  queryCache: new QueryCache({
    onError: (error) => reportQueryError(error, 'query'),
  }),
  mutationCache: new MutationCache({
    onError: (error) => reportQueryError(error, 'mutation'),
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      // Never auto-retry a DB admission gate saturation 503 (#1417 Phase 0C)
      // — the backend is shedding load on purpose, so retrying it amplifies
      // exactly the pressure it's trying to relieve. Otherwise mirrors
      // TanStack's own default (retry up to 3 times).
      retry: shouldRetryQuery,
    },
  },
})

const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <APIProvider apiKey={env.VITE_GOOGLE_MAP_API_KEY} language='fr'>
        <QueryClientProvider client={queryClient}>
          <AppToaster />
          <SidebarProvider>
            <GuidedTourProvider>
              <App />
            </GuidedTourProvider>
          </SidebarProvider>
        </QueryClientProvider>
      </APIProvider>
    </StrictMode>
  )
}
